import { Claim } from '@/api/claims'
import { Row } from '@/components'
import { Typography } from '@/components/atoms/Typography/Component'
import { Pagination } from '@/components/molecules/Pagination'
import { FeatureFlag } from '@/enums/featureFlags'
import { useUnreadCount } from '@/services'
import { useFeatureFlag } from '@/services/feature-flags'
import { DataTable, useDataTable } from '@surein/ui'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClaimsLocationGroup } from '../useCompaniesClaims'
import createColumns from './columns'
import { StyledRelativeContainer, TableContainer } from './styles'

type ClaimsTableProps = { location: ClaimsLocationGroup }

const ClaimsTable = ({ location }: ClaimsTableProps) => {
  const { t } = useTranslation()
  const { data: claimsOverviewFeatureFlag } = useFeatureFlag(
    FeatureFlag.CLAIMS_OVERVIEW,
  )
  const isClaimsOverviewEnabled = !!claimsOverviewFeatureFlag?.state
  const { data: unreadCount } = useUnreadCount(location.companyId)
  const unreadByCase = unreadCount?.by_case

  const columns = useMemo(
    () =>
      createColumns(
        t,
        location.companyId,
        isClaimsOverviewEnabled,
        unreadByCase ?? {},
      ),
    [t, location.companyId, isClaimsOverviewEnabled, unreadByCase],
  )

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  const table = useDataTable<Claim>({
    columns: columns,
    data: location.claims,
    configOverrides: {
      state: {
        pagination,
      },
      onPaginationChange: setPagination,
      enableFilters: true,
      enableColumnFilters: true,
      globalFilterFn: 'includesString',
    },
  })

  const pageCount = table.getPageCount()
  const hasData = location.claims.length > 0

  return (
    <TableContainer hasData={hasData}>
      <Row gap="16px" justifyContent="space-between">
        <Typography bold variant="p1Body">
          {location.locationName}
        </Typography>
      </Row>
      <StyledRelativeContainer>
        <DataTable
          table={table}
          borderless
          emptyText={t('claimsPage.table.empty')}
        />
        {hasData && (
          <Pagination
            pageIndex={pagination.pageIndex}
            pageCount={pageCount}
            onPageChange={(index) => table.setPageIndex(index)}
            onPageSizeChange={(size) => table.setPageSize(size)}
          />
        )}
      </StyledRelativeContainer>
    </TableContainer>
  )
}

export default ClaimsTable
